import dayjs from "dayjs";

/** 30‑minute slot index 0..47 (00:00 → 0, 00:30 → 1, …). */
function buildAllHalfHourTimeOptions() {
  const allTimes = [];
  for (let i = 0; i < 48; i++) {
    const h = Math.floor(i / 2);
    const m = (i % 2) * 30;
    const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
    const label = dayjs().hour(h).minute(m).format("h:mma");
    allTimes.push({ value, label });
  }
  return allTimes;
}

const DEFAULT_LIST_START_SLOT = 12; // 6:00 AM — fallback / legacy static list

/**
 * Map account `working_hours_start` (HH:mm on :00/:30) to slot index for ordering the dropdown.
 */
export function workingHoursStartToSlotIndex(workingHoursStart) {
  if (workingHoursStart == null || String(workingHoursStart).trim() === "") {
    return DEFAULT_LIST_START_SLOT;
  }
  const parts = String(workingHoursStart).trim().split(":");
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1] ?? "0", 10);
  if (!Number.isFinite(h) || h < 0 || h > 23) return DEFAULT_LIST_START_SLOT;
  const half = m >= 30 ? 1 : 0;
  return Math.min(47, Math.max(0, h * 2 + half));
}

/**
 * Due/time dropdown options: "—" then all half hours, list starting at `startSlotIndex` (full 24h, rotated).
 */
export function buildTimeOptionsFromSlotStart(startSlotIndex) {
  const allTimes = buildAllHalfHourTimeOptions();
  const idx =
    Number.isFinite(startSlotIndex) && startSlotIndex >= 0 && startSlotIndex < 48
      ? Math.floor(startSlotIndex)
      : DEFAULT_LIST_START_SLOT;
  const reordered = [...allTimes.slice(idx), ...allTimes.slice(0, idx)];
  return [{ value: "", label: "—" }, ...reordered];
}

export function buildTimeOptions15Min() {
  return buildTimeOptionsFromSlotStart(DEFAULT_LIST_START_SLOT);
}

/** Task/event due time lists: always ordered from 12:00 AM; caller sets default selection (e.g., working hours start). */
export function buildTimeOptionsForWorkingStart(_workingHoursStart) {
  return buildTimeOptionsFromSlotStart(0);
}

export const TIME_OPTIONS_15 = buildTimeOptions15Min();

/** Raw ISO / serialized form for probing (matches DB + JSON round-trip). */
function dueDateIsoProbe(dueDate) {
  if (dueDate == null) return "";
  if (dueDate instanceof Date && !Number.isNaN(dueDate.getTime())) {
    return dueDate.toISOString();
  }
  return String(dueDate).trim();
}

/**
 * True when due_date is stored as UTC midnight — canonical "date only" from getDueDateForApi(date, '').
 * Local display must use the UTC calendar day, not local evening (e.g. avoid 03/31 7:00 PM for 2026-04-01Z).
 */
export function isUtcDateOnlyMidnight(dueDate) {
  const isoProbe = dueDateIsoProbe(dueDate);
  if (!isoProbe) return false;
  return (
    /^\d{4}-\d{2}-\d{2}T00:00:00(\.\d{1,3})?Z$/i.test(isoProbe)
    || /^\d{4}-\d{2}-\d{2}T00:00:00(\.\d{1,3})?\+00:00$/i.test(isoProbe)
  );
}

function ymdFromUtcDateOnlyProbe(isoProbe) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T00:00:00/i.exec(isoProbe);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : "";
}

function formatYmdAsUs(ymd) {
  const p = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!p) return "";
  return `${p[2]}/${p[3]}/${p[1]}`;
}

export function getDueDatePart(dueDate) {
  if (dueDate == null) return "";
  const probe = dueDateIsoProbe(dueDate);
  if (isUtcDateOnlyMidnight(dueDate)) {
    return ymdFromUtcDateOnlyProbe(probe);
  }
  const d = dayjs(dueDate);
  if (!d.isValid()) return "";
  return d.format("YYYY-MM-DD");
}

/**
 * True when the calendar day of `dateLike` is strictly before today (local).
 * Uses UTC date-only helpers for stored date-only values. Missing/invalid → false.
 * Today itself is not before today.
 * @param {unknown} dateLike
 * @param {{ today?: import("dayjs").Dayjs }} [options]
 */
export function isCalendarDateBeforeToday(dateLike, options = {}) {
  const ymd = getDueDatePart(dateLike);
  if (!ymd) return false;
  const target = dayjs(ymd, "YYYY-MM-DD", true);
  if (!target.isValid()) return false;
  const today = (options.today ?? dayjs()).startOf("day");
  return target.startOf("day").isBefore(today);
}

export function getDueTimePart(dueDate) {
  if (dueDate == null) return "";
  const isoProbe = dueDateIsoProbe(dueDate);
  if (/^\d{4}-\d{2}-\d{2}T00:00:00(\.\d{1,3})?Z$/i.test(isoProbe)) {
    return "";
  }
  if (/^\d{4}-\d{2}-\d{2}T00:00:00(\.\d{1,3})?\+00:00$/i.test(isoProbe)) {
    return "";
  }
  const d = dayjs(dueDate);
  if (!d.isValid()) return "";
  if (d.hour() === 0 && d.minute() === 0) return "";
  return d.format("HH:mm");
}

/**
 * Builds a datetime string for the API in UTC (ISO with Z).
 * Treats dateStr + timeStr as the user's local date/time so it stores correctly
 * on servers in any timezone (e.g. Heroku UTC). Prevents "8am" being saved as "3am"
 * when the server interprets the string as UTC.
 */
export function getDueDateForApi(dateStr, timeStr) {
  const date = (dateStr != null && String(dateStr).trim()) ? String(dateStr).trim() : "";
  if (!date) return "";
  const time = (timeStr != null && String(timeStr).trim()) ? String(timeStr).trim() : "";
  // IMPORTANT: blank time means "date-only / not specified", NOT midnight.
  // Store a canonical ISO timestamp at UTC midnight so it round-trips across timezones
  // and `getDueTimePart()` can reliably treat it as "no time".
  if (!time) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    if (!m) return "";
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const da = parseInt(m[3], 10);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(da)) return "";
    const utcMidnight = new Date(Date.UTC(y, mo - 1, da, 0, 0, 0, 0));
    if (Number.isNaN(utcMidnight.getTime())) return "";
    return utcMidnight.toISOString();
  }

  const localStr = `${date}T${time}`;
  const d = dayjs(localStr);
  if (!d.isValid()) return "";
  return d.toISOString();
}

export function getDueDateForInput(dueDate) {
  const datePart = getDueDatePart(dueDate);
  if (!datePart) return "";
  const timePart = getDueTimePart(dueDate);
  // Do not use "00:00" fallback: that means local midnight, not date-only UTC (see getDueDateForApi).
  return getDueDateForApi(datePart, timePart);
}

export function formatDueDate(dueDate) {
  if (dueDate == null) return "";
  const probe = dueDateIsoProbe(dueDate);
  if (isUtcDateOnlyMidnight(dueDate)) {
    return formatYmdAsUs(ymdFromUtcDateOnlyProbe(probe));
  }
  const d = dayjs(dueDate);
  if (!d.isValid()) return "";
  const hasTime = d.hour() !== 0 || d.minute() !== 0;
  return hasTime ? d.format("MM/DD/YYYY h:mm A") : d.format("MM/DD/YYYY");
}

/**
 * Task table Due column — same rules as {@link formatDueDate}.
 * Stored instants at local midnight (e.g. `getDueDateForApi(date, '00:00')`) are shown as date-only,
 * not `12:00 AM`, which the old table formatter did when it always appended a clock time for non-UTC-midnight values.
 */
export function formatDueDateTimeForTable(dueDate) {
  return formatDueDate(dueDate);
}

/**
 * Dayjs (local) used for sorting and time-window filters. Date-only (UTC midnight) maps to
 * start of that calendar day in the user's local TZ so "due Apr 1" isn't ordered as Mar 31 evening.
 * Returns null if due is missing/invalid.
 */
export function getDueInstantForComparison(dueDate) {
  if (dueDate == null || dueDate === "") return null;
  if (isUtcDateOnlyMidnight(dueDate)) {
    const ymd = getDueDatePart(dueDate);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (!m) return null;
    const y = parseInt(m[1], 10);
    const mo = parseInt(m[2], 10);
    const da = parseInt(m[3], 10);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(da)) return null;
    const localMidnight = new Date(y, mo - 1, da, 0, 0, 0, 0);
    if (Number.isNaN(localMidnight.getTime())) return null;
    const d = dayjs(localMidnight);
    return d.isValid() ? d : null;
  }
  const d = dayjs(dueDate);
  return d.isValid() ? d : null;
}

/**
 * One line for the earliest task time block: date + start time (local), optionally through end time.
 * @param {string|Date|number} start
 * @param {string|Date|number} end
 * @param {number} moreCount – extra blocks not shown (appended as " (+N more)")
 * @param {{ includeEndTime?: boolean, dateOnly?: boolean }} [options]
 *   - includeEndTime: default true; false = date + start only (e.g. task table Scheduled column)
 *   - dateOnly: true = calendar date only (`MM/DD/YYYY`), omit times (compact tables)
 */
export function formatScheduledSlot(start, end, moreCount = 0, options) {
  const includeEndTime = options?.includeEndTime !== false;
  const dateOnly = Boolean(options?.dateOnly);
  if (start == null || start === "") return "";
  const s = dayjs(start);
  if (!s.isValid()) return "";
  const e = end != null && end !== "" ? dayjs(end) : null;
  const suffix = moreCount > 0 ? ` (+${moreCount} more)` : "";
  if (dateOnly) {
    return `${s.format("MM/DD/YYYY")}${suffix}`;
  }
  const hasEnd = e && e.isValid();
  if (includeEndTime && hasEnd) {
    const sameCalDay = s.format("YYYY-MM-DD") === e.format("YYYY-MM-DD");
    if (sameCalDay) {
      return `${s.format("MM/DD/YYYY")} ${s.format("h:mm A")} – ${e.format("h:mm A")}${suffix}`;
    }
    return `${s.format("MM/DD/YYYY h:mm A")} – ${e.format("MM/DD/YYYY h:mm A")}${suffix}`;
  }
  return `${s.format("MM/DD/YYYY h:mm A")}${suffix}`;
}
